import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import { TrashIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/solid';
import { LineItem as CartLineItem } from 'shared/types/cart/LineItem';
import { Variant } from 'shared/types/product';
import Image from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import OutOfStock from 'components/commercetools-ui/atoms/out-of-stock';
import Typography from 'components/commercetools-ui/atoms/typography';
import DiscountBadge from 'components/commercetools-ui/molecules/badge/DiscountBadge';
import BundleIcon from 'components/icons/bundle-icon';
import EbookIcon from 'components/icons/ebook-icon';
import PhysicalBookIcon from 'components/icons/physical-book-icon';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { getSelectedBundleLabel, TEMP_ATTRIBUTE_FOR_BUNDLE } from 'helpers/utils/bundlesItems';
import { getDiscount } from 'helpers/utils/getDiscount';
import { LineItem } from 'types/entity/cart';
import ProductVariantPrice from '../../product/product-details/components/product-variant-price';

interface ClassNames {
  moveToWishlist?: string;
}
interface Props {
  item: CartLineItem;
  classNames?: ClassNames;
  onRemoveItem(): Promise<void>;
  onUpdateItem(quantity: number): Promise<void>;
  OnMoveToWishlist(): Promise<void>;
}

const CartItem: React.FC<Props> = ({ item, onRemoveItem, onUpdateItem, OnMoveToWishlist, classNames = {} }) => {
  const { locale } = useParams();

  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });

  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  const [processing, setProcessing] = useState(false);

  const [quantity, setQuantity] = useState<number>(item.count || 1);

  const [showQtyExceeded, setShowQtyExceeded] = useState(false);

  const bundles = item?.variant?.attributes?.[TEMP_ATTRIBUTE_FOR_BUNDLE];

  const deleteButtonClassName = useClassNames([
    'size-24 text-gray-500 hover:text-gray-900',
    processing ? 'cursor-not-allowed' : 'cursor-pointer',
  ]);

  const wishlistButtonClassName = useClassNames([
    'text-xs text-blue-400 underline border-l pl-10 md:pl-20',
    classNames.moveToWishlist,
    processing ? 'cursor-not-allowed' : 'cursor-pointer',
  ]);

  const handleUpdateItem = useCallback(
    async (newQuantity: number) => {
      if (processing) return;

      setProcessing(true);
      await (newQuantity < 1 ? onRemoveItem() : onUpdateItem(newQuantity));
      setProcessing(false);
    },
    [onUpdateItem, onRemoveItem, processing],
  );

  const updateQuantity = (newQuantity: number) => {
    setShowQtyExceeded(newQuantity > 99);
    newQuantity = Math.min(99, Math.max(1, newQuantity));
    setQuantity(newQuantity);
    handleUpdateItem(newQuantity);
  };

  const applyQuantityChange = () => updateQuantity(quantity);

  const handleRemoveItem = useCallback(async () => {
    if (processing) return;

    setProcessing(true);
    await onRemoveItem();
    setProcessing(false);
  }, [onRemoveItem, processing]);

  const handleMoveToWishlist = useCallback(async () => {
    if (processing) return;

    setProcessing(true);
    await OnMoveToWishlist();
    setProcessing(false);
  }, [OnMoveToWishlist, processing]);

  const { hasDiscount, discountPercentage } = getDiscount(item?.variant ?? ({} as Variant));

  return (
    <div className="flex max-w-full items-stretch justify-start gap-10 p-16 md:gap-15">
      <div className="w-125 shrink-0 bg-white p-12">
        <div className="relative size-full rounded-sm">
          {item?.variant?.images?.[0] ? (
            <Image src={item?.variant?.images?.[0]} suffix="small" style={{ objectFit: 'contain' }} fill />
          ) : (
            <Image
              className="w-full rounded-sm object-contain group-hover:opacity-75"
              src="/images/imagen.png"
              alt="Product coming soon image in cart"
            />
          )}

          <div className="absolute left-0 top-0 z-10 w-full">
            <div className="w-full text-center">
              {hasDiscount && <DiscountBadge discountPercentage={discountPercentage} />}
            </div>
            <div className="w-full text-center">
              {!item.variant?.isOnStock && (
                <div className="mb-8">
                  <OutOfStock />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="relative max-w-full grow overflow-hidden">
        <div className="flex items-center gap-6">
          {item?.productTypeKey === 'physical-book' ? (
            <div className="flex items-center gap-6">
              <PhysicalBookIcon className="size-14" />
              <Typography className="text-xs"> Printed Book</Typography>
            </div>
          ) : item?.productTypeKey === 'ebook' ? (
            <div className="flex items-center gap-6">
              <EbookIcon className="size-14" />
              <Typography className="text-xs">eBook</Typography>
            </div>
          ) : item?.productTypeKey === 'bundle' ? (
            <div className="flex items-center gap-6">
              <BundleIcon className="size-14" />
              <Typography className="text-xs">Book & eBook</Typography>
            </div>
          ) : (
            <div className="flex items-center gap-6">
              <Typography className="text-xs">
                {item?.productTypeKey
                  ?.replace(/-/g, ' ')
                  .split(' ')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}
              </Typography>
            </div>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <Link
            link={item._url}
            className="line-clamp-2 max-w-[90%] overflow-hidden text-ellipsis text-xs font-bold leading-normal text-blue-600 underline-offset-4 hover:underline focus:underline active:underline md:mt-6 md:text-base lg:text-lg"
            title={item.name}
          >
            {item.name}
          </Link>
        </div>

        <div className="relative mt-2 flex flex-wrap pr-40 text-base font-normal leading-none text-gray-500">
          {item?.variant?.attributes?.['author'] && (
            <>
              <Typography className="mr-4 text-xs md:text-sm">Author:</Typography>
              <Typography className="mr-8 whitespace-nowrap border-r border-gray-500 pr-8 text-xs text-blue-600 md:text-sm">
                {item?.variant?.attributes?.['author']}
              </Typography>
            </>
          )}
          {(item?.variant?.attributes?.['publicationYear'] ||
            item?.variant?.attributes?.['publicationMonth'] ||
            item?.variant?.attributes?.['publicationDay']) && (
            <>
              <Typography className="mr-4 whitespace-nowrap text-xs md:text-sm">Publication date: </Typography>
              <Typography className="whitespace-nowrap text-xs text-primary-black md:text-sm">
                {[
                  `${item?.variant?.attributes?.['publicationYear']}`,
                  item?.variant?.attributes?.['publicationMonth'] && item?.variant?.attributes?.['publicationMonth'],
                  item?.variant?.attributes?.['publicationDay'] && item?.variant?.attributes?.['publicationDay'],
                ]
                  .filter(Boolean)
                  .join('-')}
              </Typography>
            </>
          )}
        </div>
        {/* {!item?.variant?.isOnStock && (
          <div className="mt-8 text-xs md:text-sm">
            <OutOfStock />
          </div>
        )} */}

        {!!bundles?.length && (
          <div className="flex flex-col">
            {bundles.map((bundle: LineItem) => (
              <div key={bundle.lineItemId}>
                <span className="text-xs font-semibold md:text-sm">{`+ ${bundle.productTypeKey}: `}</span>
                <span className="text-xs md:text-sm">
                  {getSelectedBundleLabel(bundle.variant, bundle.productTypeKey)}
                </span>
                <span className="ml-4 text-xs text-gray-400 md:text-sm">
                  {CurrencyHelpers.formatForCurrency(bundle.price ?? 0, locale)}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-8 pt-14">
          <ProductVariantPrice variant={item?.variant} inModalVersion className="text-12 md:text-base" />
        </div>
        <div className="mt-10 flex items-center justify-between">
          <div className="flex items-center gap-10 md:gap-20">
            <div className="flex items-center gap-12">
              {/* Decrease Button */}
              <button
                aria-label="decrease quantity"
                className="flex items-center justify-center rounded-3xl border border-black p-4 hover:bg-neutral-100 disabled:opacity-30"
                onClick={() => {
                  updateQuantity(Math.max(1, quantity - 1));
                }}
                disabled={quantity <= 1}
              >
                <MinusIcon width={17.5} strokeWidth={2} className="size-10" />
              </button>
              <input
                aria-label="Quantity"
                type="number"
                min="1"
                max="99"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value) || 1)}
                onBlur={applyQuantityChange}
                onKeyDown={(e) => e.key === 'Enter' && applyQuantityChange()}
                className="rounded border-y border-gray-600 px-4 py-2 text-center text-sm [appearance:textfield] md:px-10 md:py-4 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <button
                aria-label="increase quantity"
                className="flex items-center justify-center rounded-3xl border border-black p-4 hover:bg-neutral-100 disabled:opacity-30"
                onClick={() => {
                  updateQuantity(Math.min(99, quantity + 1));
                }}
                disabled={quantity >= 99}
              >
                <PlusIcon width={17.5} strokeWidth={2} className="size-10" />
              </button>
            </div>

            <p className={wishlistButtonClassName} onClick={handleMoveToWishlist}>
              {formatCartMessage({ id: 'move.to.wishlist', defaultMessage: 'Save for later' })}
            </p>
          </div>
          <TrashIcon onClick={handleRemoveItem} className={deleteButtonClassName} />
          {/* <i onClick={handleRemoveItem} className={deleteButtonClassName}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
              <path
                d="M4.225 0.554102L4 1.00098H1C0.446875 1.00098 0 1.44785 0 2.00098C0 2.5541 0.446875 3.00098 1 3.00098H13C13.5531 3.00098 14 2.5541 14 2.00098C14 1.44785 13.5531 1.00098 13 1.00098H10L9.775 0.554102C9.60625 0.213477 9.25938 0.000976562 8.88125 0.000976562H5.11875C4.74062 0.000976562 4.39375 0.213477 4.225 0.554102ZM13 4.00098H1L1.6625 14.5947C1.7125 15.3854 2.36875 16.001 3.15937 16.001H10.8406C11.6312 16.001 12.2875 15.3854 12.3375 14.5947L13 4.00098Z"
                fill="#1F1F1F"
              />
            </svg>
          </i> */}
        </div>
        {showQtyExceeded && (
          <div className="mt-10 flex items-center justify-between">
            <Typography className="text-12 font-semibold text-red-600 md:text-14">
              {formatProductMessage({
                id: 'quantity.exceeded',
                defaultMessage: 'For bulk orders of 100 copies or more, call us.',
              })}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartItem;
